/* Phase 3B — Batch 7 (P3-7 Treasury) mock/domain data.
   Sources: prototype/pages/treasury/*.html (7 prototypes) plus the canonical
   wireframe mocks (specs/06-treasury-operations/mocks/*.html).
   Screens below remain PROTOTYPE_BACKED pending the audited re-implementation;
   the canonical entity registries appended at the bottom of this file are the
   Treasury Data Foundation (see analysis/TREASURY_FLOW_AUDIT.md §30).
   Fixtures are explicitly illustrative — preserved as copy, never computed.

   WLT-ILL-001..004 have been RETIRED (audit data-model debt): Treasury now
   references the canonical wallet universe WLT-001..007 (data/mock.ts). */

export type TreasuryTone = "success" | "warning" | "danger" | "info" | "pending" | "neutral";

/* ─────────────── TRS-00 Treasury Home ─────────────── */

export const TREASURY_KPIS = [
  { value: "$9.36M", label: "Total AUM", tone: "default" as const },
  { value: "0 ETH", label: "Staked", tone: "neutral" as const },
  { value: "$0", label: "Yield MTD", tone: "neutral" as const },
  { value: "$—", label: "Avg Cost Basis", tone: "neutral" as const },
];

export const ALLOCATION = [
  { name: "ETH", pct: 42.4, value: "$3.97M", fill: "var(--ads-viz-indigo)" },
  { name: "USDC", pct: 26.7, value: "$2.5M", fill: "var(--ads-status-success)" },
  { name: "BTC", pct: 22.4, value: "$2.1M", fill: "var(--ads-status-warning)" },
  { name: "SOL", pct: 9.5, value: "$890K", fill: "var(--ads-status-pending)" },
];

export interface PortfolioRow {
  wallet: string;
  asset: string;
  amount: string;
  valueUsd: string;
  pct: string;
  custody: string;
}

export const PORTFOLIO: PortfolioRow[] = [
  { wallet: "WLT-001", asset: "ETH", amount: "1,240", valueUsd: "$3,970,000", pct: "42.4%", custody: "Fireblocks" },
  { wallet: "WLT-002", asset: "USDC", amount: "2,500,000", valueUsd: "$2,500,000", pct: "26.7%", custody: "Mesh (VaultCore)" },
  { wallet: "WLT-003", asset: "BTC", amount: "48.7", valueUsd: "$2,100,000", pct: "22.4%", custody: "BitGo" },
  { wallet: "WLT-007", asset: "SOL", amount: "12,400", valueUsd: "$890,000", pct: "9.5%", custody: "Mesh (VaultCore)" },
];

/* WLT-007 (HT Solana Ops) is pending activation in the wallet universe
   (balanceUsd null); the SOL balance shown above is the treasury's expected
   holdings snapshot. Documented as an illustrative fixture (see audit §30). */

export const CUSTODIAN_HEALTH = [
  { name: "Fireblocks", tone: "success" as const, status: "Online" },
  { name: "Mesh (VaultCore)", tone: "success" as const, status: "Online" },
  { name: "BitGo", tone: "success" as const, status: "Online" },
  { name: "Coinbase Prime", tone: "warning" as const, status: "Degraded" },
];

/* ─────────────── TRS-01 Opportunity Queue ─────────────── */

export const OPPORTUNITY_KPIS = [
  { value: "7", label: "Open Opportunities", tone: "default" as const },
  { value: "9.2%", label: "Total Estimated APR", tone: "success" as const },
  { value: "8.4M", label: "Unallocated Capital", tone: "default" as const },
  { value: "2", label: "Maturing Soon", tone: "warning" as const },
];

export type OppRisk = "Low" | "Medium" | "High";
export type OppType = "Liquid Staking" | "Native" | "Compound V3" | "Aave V3" | "sDAI";

export interface Opportunity {
  name: string;
  network: string;
  type: OppType;
  typeTone: TreasuryTone;
  apr: string;
  aprTone: TreasuryTone;
  lock: string;
  capital: string;
  risk: OppRisk;
  riskTone: TreasuryTone;
}

export const OPPORTUNITIES: Opportunity[] = [
  { name: "Staking — ETH", network: "Ethereum", type: "Liquid Staking", typeTone: "info", apr: "5.8%", aprTone: "success", lock: "Flexible", capital: "1,500 ETH", risk: "Low", riskTone: "success" },
  { name: "Staking — POL", network: "Polygon", type: "Native", typeTone: "pending", apr: "7.1%", aprTone: "success", lock: "21d", capital: "250,000 POL", risk: "Medium", riskTone: "warning" },
  { name: "Lending — USDC", network: "Arbitrum", type: "Compound V3", typeTone: "info", apr: "4.9%", aprTone: "success", lock: "Flexible", capital: "3,000,000 USDC", risk: "Low", riskTone: "success" },
  { name: "Lending — USDT", network: "Ethereum", type: "Aave V3", typeTone: "info", apr: "5.2%", aprTone: "success", lock: "Flexible", capital: "1,250,000 USDT", risk: "Low", riskTone: "success" },
  { name: "Yield — DAI", network: "Ethereum", type: "sDAI", typeTone: "neutral", apr: "8.4%", aprTone: "warning", lock: "Flexible", capital: "2,000,000 DAI", risk: "Medium", riskTone: "warning" },
  { name: "Staking — DOT", network: "Polkadot", type: "Native", typeTone: "pending", apr: "12.6%", aprTone: "success", lock: "28d", capital: "180,000 DOT", risk: "High", riskTone: "danger" },
];

/* ─────────────── TRS-02 Staking Positions ─────────────── */

export const STAKING_OPTIONS = [
  { asset: "ETH", symbol: "Ξ", color: "var(--ads-viz-indigo)", provider: "Liquid Staking via Lido", apy: "▲ APY ~3.8%", minStake: "0.01 ETH", lock: "None (liquid)", available: "1,240 ETH", enabled: true },
  { asset: "SOL", symbol: "◎", color: "var(--ads-status-pending)", provider: "Native Staking", apy: "▲ APY ~6.2%", minStake: "1 SOL", lock: "~2-4 days", available: "12,400 SOL", enabled: true },
  { asset: "ADA", symbol: "₳", color: "var(--ads-asset-ada)", provider: "Cardano Delegation", apy: "▲ APY ~4.1%", minStake: "5 ADA", lock: "~5 days", available: "—", enabled: false },
];

export const STAKING_HISTORY_NOTE = "No staking history yet.";

export const STAKING_MODAL = {
  title: "Enable ETH Staking",
  desc: "Configure liquid staking via Lido Finance for your ETH holdings. Staked ETH is represented as stETH and earns ~3.8% APY.",
  rows: [
    { k: "Available to stake", v: "1,240 ETH" },
    { k: "Estimated annual yield", v: "~47.1 ETH/yr" },
    { k: "Provider fee", v: "10% of rewards" },
  ],
  submitToast: "Staking configuration requires approval. A policy approval request would be created.",
};

/* ─────────────── TRS-03 Create Staking Intent ─────────────── */

export const STAKING_CREATE_FIELDS = {
  network: "Ethereum",
  protocol: "Liquid Staking (Lido)",
  amount: "1500",
  lock: "Flexible (withdrawal anytime)",
  reason: "Allocate idle treasury capital per ALY-118",
  availableBalance: "8,420 ETH",
  annualYield: "87 ETH (~$174,000)",
  protocolHint: "Current liquid staking APR: 5.8%",
  policyNote: "Policy check: > 1,000 ETH requires a second approver. Purpose field will be attached to the intent record.",
};

export const STAKING_CREATE_REVIEW = {
  rows: [
    { k: "Network", v: "Ethereum" },
    { k: "Protocol", v: "Liquid Staking (Lido)" },
    { k: "Amount", v: "1,500 ETH" },
    { k: "Lock", v: "Flexible" },
    { k: "Est. APR", v: "5.8%" },
    { k: "Est. Annual Yield", v: "87 ETH ≈ $174,000" },
    { k: "Intent Reason", v: "ALY-118" },
  ],
  policyBadge: "2-approver policy applies",
  note: "Submitting this intent routes it to the approval workflow (TRF-class). No funds move until approval quorum is met.",
  submitToast: "Staking intent submitted for approval",
};

/* ─────────────── TRS-04 Lending Overview ─────────────── */

export const LENDING_KPIS = [
  { value: "$2.85M", label: "Total Lent", tone: "default" as const },
  { value: "$14,230", label: "Interest Earned MTD", tone: "success" as const, change: "▲ +$1,840 vs last month" },
  { value: "3", label: "Active Positions", tone: "default" as const, sub: "1 maturing in 12 days" },
  { value: "5.84%", label: "Weighted APY", tone: "success" as const, change: "▲ +0.2% vs prior period" },
];

export interface LendingPosition {
  protocol: string;
  protocolAbbr: string;
  protocolColor: string;
  protocolType: string;
  asset: string;
  principal: string;
  rate: string;
  rateTone: TreasuryTone;
  interest: string;
  maturity: string;
  status: string;
  statusTone: TreasuryTone;
  withdrawable: boolean;
  renewable: boolean;
}

export const LENDING_POSITIONS: LendingPosition[] = [
  { protocol: "Aave v3", protocolAbbr: "A", protocolColor: "var(--ads-status-info)", protocolType: "DeFi — Ethereum", asset: "USDC", principal: "$1,500,000", rate: "6.12%", rateTone: "success", interest: "$7,650", maturity: "Open-ended", status: "Active", statusTone: "success", withdrawable: true, renewable: false },
  { protocol: "Compound III", protocolAbbr: "C", protocolColor: "var(--ads-status-success)", protocolType: "DeFi — Ethereum", asset: "USDT", principal: "$847,500", rate: "5.41%", rateTone: "success", interest: "$3,840", maturity: "Open-ended", status: "Active", statusTone: "success", withdrawable: true, renewable: false },
  { protocol: "Institutional Desk", protocolAbbr: "I", protocolColor: "var(--ads-status-pending)", protocolType: "OTC Credit Line", asset: "USDC", principal: "$500,000", rate: "5.75%", rateTone: "success", interest: "$2,740", maturity: "Sep 24, 2026 · 12 days remaining", status: "Maturing", statusTone: "warning", withdrawable: false, renewable: true },
];

export const LENDING_PROTOCOLS = [
  { name: "Aave v3", abbr: "A", color: "var(--ads-status-info)", type: "DeFi Lending", usdc: "6.12%", usdt: "5.89%", min: "$10,000", available: true },
  { name: "Compound III", abbr: "C", color: "var(--ads-status-success)", type: "DeFi Lending", usdc: "5.41%", usdt: "5.28%", min: "$5,000", available: true },
  { name: "Morpho Blue", abbr: "M", color: "var(--ads-status-pending)", type: "DeFi Lending", usdc: "—", usdt: "—", min: "—", available: false },
];

export const CREDIT_FACILITY = { limit: "$5,000,000", deployed: "$500,000", available: "$4,500,000", utilization: "10%" };

export const LENDING_MODALS = {
  newTitle: "New Lending Position",
  newDesc: "Deploy capital to a lending protocol. All positions require policy approval before execution.",
  withdrawTitle: "Withdraw from Aave v3",
  withdrawDesc: "Withdraw principal and accrued interest from this lending position.",
  withdrawPrincipal: "$1,500,000",
  withdrawInterest: "$7,650",
  withdrawFee: "None",
  withdrawSettlement: "~2 min (on-chain)",
  renewTitle: "Renew Credit Line Position",
  renewDesc: "Renew the institutional credit line position maturing Sep 24, 2026.",
  renewWarning: "⚠ This position matures in 12 days. Renew to avoid automatic settlement.",
  submitToast: "Lending position requires policy approval. Approval request APR-2024-0042 created.",
  withdrawToast: "Withdrawal initiated. Funds will arrive in destination wallet within 2 minutes.",
  renewToast: "Renewal request submitted for approval.",
};

/* ─────────────── TRS-05 Liquidity Dashboard ─────────────── */

export const LIQUIDITY_KPIS = [
  { value: "$4.12M", label: "Total Liquidity", tone: "default" as const },
  { value: "$3,240", label: "24h Fees Earned", tone: "success" as const, change: "▲ +18% vs yesterday" },
  { value: "4", label: "Active Pools", tone: "danger" as const, sub: "1 out of range" },
  { value: "−0.42%", label: "Avg Impermanent Loss", tone: "danger" as const, change: "▼ Worsened vs prior week" },
];

export interface LiquidityPosition {
  pair: string;
  iconPair: { symbol: string; color: string }[];
  feeTier: string;
  venue: string;
  venueTone: TreasuryTone;
  liquidity: string;
  apy: string;
  apyTone: TreasuryTone;
  il: string;
  ilTone: TreasuryTone;
  range: string;
  rangeStatus: string;
  rangeTone: TreasuryTone;
  status: string;
  statusTone: TreasuryTone;
  actionable: string;
}

export const LIQUIDITY_POSITIONS: LiquidityPosition[] = [
  { pair: "USDC/ETH", iconPair: [{ symbol: "$", color: "var(--ads-asset-usdc)" }, { symbol: "Ξ", color: "var(--ads-asset-eth)" }], feeTier: "0.05% fee tier", venue: "Uniswap v3", venueTone: "info", liquidity: "$1,800,000", apy: "—", apyTone: "neutral", il: "−1.24%", ilTone: "warning", range: "$2,100–$2,400", rangeStatus: "Out of range", rangeTone: "danger", status: "Rebalance", statusTone: "warning", actionable: "Rebalance" },
  { pair: "USDC/USDT", iconPair: [{ symbol: "$", color: "var(--ads-asset-usdc)" }, { symbol: "₮", color: "var(--ads-asset-usdt)" }], feeTier: "0.01% fee tier · Stable", venue: "Curve Finance", venueTone: "info", liquidity: "$1,500,000", apy: "4.21%", apyTone: "success", il: "−0.02%", ilTone: "success", range: "$0.999–$1.001", rangeStatus: "In range", rangeTone: "success", status: "Active", statusTone: "success", actionable: "Remove" },
  { pair: "WBTC/ETH", iconPair: [{ symbol: "₿", color: "var(--ads-asset-btc)" }, { symbol: "Ξ", color: "var(--ads-asset-eth)" }], feeTier: "0.30% fee tier", venue: "Uniswap v3", venueTone: "info", liquidity: "$620,000", apy: "8.14%", apyTone: "success", il: "−0.88%", ilTone: "warning", range: "0.062–0.071 BTC/ETH", rangeStatus: "In range", rangeTone: "success", status: "Active", statusTone: "success", actionable: "Remove" },
  { pair: "USDC/SOL", iconPair: [{ symbol: "$", color: "var(--ads-asset-usdc)" }, { symbol: "◎", color: "var(--ads-asset-sol)" }], feeTier: "Dynamic fee", venue: "Orca Whirlpools", venueTone: "info", liquidity: "$200,000", apy: "12.3%", apyTone: "success", il: "−0.14%", ilTone: "success", range: "$148–$165", rangeStatus: "In range", rangeTone: "success", status: "Active", statusTone: "success", actionable: "Remove" },
];

export const VENUES = [
  { name: "Uniswap v3", abbr: "U", color: "var(--ads-status-info)", protocol: "Ethereum · Concentrated Liquidity", tvl: "$4.1B", tiers: "0.01%, 0.05%, 0.3%, 1%", yours: "2 of yours", available: true },
  { name: "Curve Finance", abbr: "C", color: "var(--ads-status-success)", protocol: "Ethereum · Stable pools", tvl: "$2.8B", tiers: "Stablecoin pairs", yours: "1 of yours", available: true },
  { name: "Orca Whirlpools", abbr: "O", color: "var(--ads-status-pending)", protocol: "Solana · Concentrated", tvl: "$380M", tiers: "SOL ecosystem", yours: "1 of yours", available: true },
];

export const LIQUIDITY_MODALS = {
  addTitle: "Add Liquidity",
  addDesc: "Deploy capital into a liquidity pool. Concentrated liquidity positions require a price range.",
  rebalanceTitle: "Rebalance USDC/ETH Position",
  rebalanceDesc: "Your concentrated liquidity position on Uniswap v3 is out of range. Rebalancing will withdraw, reswap, and re-enter at the current price.",
  rebalanceWarning: "Current ETH price: $2,560 · Your range: $2,100–$2,400 · Status: Above range",
  removeTitle: "Remove Liquidity",
  removeDesc: "Withdraw your liquidity position from Curve USDC/USDT. Accrued fees will be collected automatically.",
  submitToast: "Liquidity position requires policy approval. Approval request created.",
  rebalanceToast: "Rebalance submitted for approval. APR-2024-0043 created.",
  removalToast: "Removal requires policy approval. APR-2024-0044 created.",
  total30d: "$11,490",
  projectedAnnual: "$137,880",
};

export const REBALANCE_WARNING = {
  title: "Rebalance Recommended — USDC/ETH Pool",
  desc: "Current price has moved outside your configured range on the Uniswap v3 USDC/ETH position. Your liquidity is no longer earning fees. Consider rebalancing to capture the current price range.",
};

/* ─────────────── TRS-06 Route Comparison ─────────────── */

export const ROUTE_INTRO = {
  amount: "1,500 ETH",
  from: "Treasury Main",
  to: "Custody · Warm Hot",
  evaluated: "4 routes",
};

export interface RouteOption {
  id: string;
  title: string;
  sub: string;
  recommended: boolean;
  fee: string;
  feeTone: TreasuryTone;
  latency: string;
  latencyTone: TreasuryTone;
  success: string;
  successTone: TreasuryTone;
  gas: string;
  compliance: string;
  complianceTone: TreasuryTone;
  slippage: string;
};

export const ROUTES: RouteOption[] = [
  { id: "A", title: "Route A — Direct REST", sub: "Custody REST · HTTPS/mTLS · single-hop", recommended: true, fee: "$0", feeTone: "success", latency: "2.4s", latencyTone: "neutral", success: "99.9%", successTone: "success", gas: "— (off-chain custody)", compliance: "In-bank path (fully controlled)", complianceTone: "success", slippage: "0.00%" },
  { id: "B", title: "Route B — Multi-hop DeFi", sub: "Aave V3 withdrawal → aggregator → destination", recommended: false, fee: "0.12% ($2,184)", feeTone: "warning", latency: "8.7s", latencyTone: "warning", success: "97.4%", successTone: "warning", gas: "~0.014 ETH", compliance: "Crosses external contracts", complianceTone: "warning", slippage: "0.10% worst case" },
  { id: "C", title: "Route C — Bridge via LayerZero", sub: "Cross-chain bridge — requires 2-network approval", recommended: false, fee: "0.08% ($1,456)", feeTone: "warning", latency: "11.9s", latencyTone: "warning", success: "96.8%", successTone: "warning", gas: "~0.021 ETH + bridge fee", compliance: "External bridge contract", complianceTone: "danger", slippage: "0.20% worst case" },
];

export const ROUTE_ACTIONS = {
  approve: "Approve Route A & Proceed",
  approveHref: "/treasury/opportunities",
  exportToast: "Route audit exported for compliance review",
};

/* ════════════════════════════════════════════════════════════════════════════
   ■ TREASURY DATA FOUNDATION  (Task 1 — implementation mode)
   Canonical entity registry. Source: specs/06-treasury-operations/mocks/*.html
   (treasury-home / staking / lending / liquidity wireframes, verbatim copy).

   Convention — every live/backend fact carries a `source` discriminant:
     "static-fixture"           → canonical wireframe value (front-end render source)
     "api/backend-dependency"   → resolved at runtime by a backend service
                                  (quotes, venue status, APY, health factor,
                                  settlement/finality, epochs).
   NO business logic is computed in this file (no APY math, no health-factor
   formulas, no quotes, no routing/execution/permission engines). Numbers are
   canonical copy only. IDs reused from existing registries: WLT-001..007,
   POL-001..005, EVD-006, EVL-0139/EVL-0133 (frozen Evidence/Governance refs).
   WLT-ILL-001..004 are retired and NOT referenced here.
   ════════════════════════════════════════════════════════════════════════════ */

export type TrsSource = "static-fixture" | "api/backend-dependency";

/* ── TRS-00 Treasury Home ─────────────────────────────────────────────── */

export interface TrsHomeMetrics {
  totalAum: string;
  aumWallets: string;
  idleBalance: string;
  idlePct: string;
  activeStaking: string;
  stakingCount: string;
  activeLending: string;
  lendingCount: string;
  venueExposure: string;
  venueCount: string;
  riskFlag: { subject: string; detail: string; source: TrsSource };
  topStaking: { asset: string; amount: string; apy: string; wallet: string }[];
  venueBalances: { venueId: string; venue: string; balance: string; pct: string }[];
}

export const TRS_HOME_METRICS: TrsHomeMetrics = {
  totalAum: "$12,400,000",
  aumWallets: "8 wallets",
  idleBalance: "$3,200,000",
  idlePct: "25.8% of AUM",
  activeStaking: "$4,100,000",
  stakingCount: "3 positions",
  activeLending: "$2,800,000",
  lendingCount: "2 positions",
  venueExposure: "$2,300,000",
  venueCount: "3 venues",
  riskFlag: {
    subject: "Margin health factor 1.15 on LND-002 (threshold: 1.25)",
    detail: "ETH price increase ($1,900 → $1,950). Assigned to Elena Vasquez (USR-004).",
    source: "api/backend-dependency",
  },
  topStaking: [
    { asset: "ETH", amount: "1,200 ($2,160,000)", apy: "3.8%", wallet: "WLT-003" },
    { asset: "SOL", amount: "18,500 ($1,110,000)", apy: "6.2%", wallet: "WLT-005" },
    { asset: "DOT", amount: "120,000 ($830,000)", apy: "11.4%", wallet: "WLT-006" },
  ],
  venueBalances: [
    { venueId: "VEN-001", venue: "Coinbase Prime", balance: "$1,200,000", pct: "9.7%" },
    { venueId: "VEN-002", venue: "Circle", balance: "$750,000", pct: "6.0%" },
    { venueId: "VEN-003", venue: "Uniswap v3", balance: "$350,000", pct: "2.8%" },
  ],
};

/* TRS-00 exception state — settlement failure register (funds locked). */
export const TRS_SETTLEMENT_FAILURES = [
  {
    intentId: "TRS-INT-0042",
    type: "Unstake",
    status: "Failed",
    error: "Validator slashing event during withdrawal",
    detail: "ETH staking withdrawal failed at execution. Funds remain locked. Contact support or retry.",
    source: "static-fixture" as TrsSource,
  },
];

/* ── TRS-01 Opportunity Queue (OPP-001..004) ──────────────────────────── */

export type TrsOppType = "Stake" | "Lend" | "Rebalance" | "Swap";
export type TrsOppStatus = "New" | "Suggested";

export interface TrsOpportunity {
  id: string;
  type: TrsOppType;
  description: string;
  estYield: string;
  risk: "Low" | "Medium";
  source: string;
  status: TrsOppStatus;
  sourceFlag: TrsSource;
}

export const TRS_OPPORTUNITIES: TrsOpportunity[] = [
  { id: "OPP-001", type: "Stake", description: "Stake 500 ETH via Lido", estYield: "3.8% APY", risk: "Low", source: "WLT-003 idle balance", status: "New", sourceFlag: "api/backend-dependency" },
  { id: "OPP-002", type: "Lend", description: "Lend 100,000 USDC on Aave v3", estYield: "5.2% APY", risk: "Medium", source: "WLT-001 idle balance", status: "New", sourceFlag: "api/backend-dependency" },
  { id: "OPP-003", type: "Rebalance", description: "Rebalance 2 BTC → ETH (target allocation drift)", estYield: "—", risk: "Low", source: "Allocation policy trigger", status: "Suggested", sourceFlag: "static-fixture" },
  { id: "OPP-004", type: "Swap", description: "Swap 50,000 MATIC → USDC", estYield: "—", risk: "Low", source: "Manual request", status: "New", sourceFlag: "static-fixture" },
];

/* OPP-001 quote is canonical-internal live data (expired at 10:30 UTC). */
export const TRS_OPP_QUOTE_STATE = {
  expired: { oppId: "OPP-001", quoteExpiredAt: "10:30 UTC", action: "Refresh Quote" },
  status: "quote-expired" as const,
};

/* ── TRS-02 / TRS-INT treasury intent registry ────────────────────────── */

export type TrsIntentState =
  | "draft"
  | "quoted-route-selected"
  | "pending-policy"
  | "pending-approval"
  | "approved"
  | "execution-pending"
  | "executed"
  | "settlement-pending"
  | "reconciled"
  | "rejected"
  | "failed-terminal";

export interface TrsIntentTimelineStep {
  state: string;
  label: string;
  ts: string;
  detail?: string;
  done: boolean;
  active: boolean;
}

export interface TrsIntent {
  id: string;
  type: "Stake" | "Unstake" | "Lend" | "Borrow" | "Swap" | "Rebalance";
  amount: string;
  sourceWallet: string;
  route: string;
  createdBy: string;
  created: string;
  state: TrsIntentState;
  stateLabel: string;
  approver?: string;
  quorum?: string;
  policyRef: string;
  timeline: TrsIntentTimelineStep[];
  sourceFlag: TrsSource;
}

export const TRS_INTENT_0051: TrsIntent = {
  id: "TRS-INT-0051",
  type: "Stake",
  amount: "500 ETH ($900,000)",
  sourceWallet: "WLT-003",
  route: "Mesh → Lido (3.8% APY)",
  createdBy: "Sai Suman Kanuganti (USR-002)",
  created: "2026-09-15 09:30 UTC",
  state: "pending-approval",
  stateLabel: "Pending Approval",
  approver: "Marcus Webb (USR-005)",
  quorum: "1 of 1",
  policyRef: "GOV-STK-01",
  sourceFlag: "static-fixture",
  timeline: [
    { state: "Draft", label: "Draft", ts: "09:30 UTC", detail: "Created by Sai Suman Kanuganti", done: true, active: false },
    { state: "Quoted / Route Selected", label: "Quoted / Route Selected", ts: "09:32 UTC", detail: "Mesh/Lido route selected, 3.8% APY quoted", done: true, active: false },
    { state: "Pending Policy", label: "Pending Policy", ts: "09:32 UTC", detail: "Policy GOV-STK-01 evaluated: PASS", done: true, active: false },
    { state: "Pending Approval", label: "Pending Approval", ts: "09:33 UTC", detail: "Sent to Marcus Webb (USR-005). Quorum: 1 of 1.", done: false, active: true },
    { state: "Approved", label: "Approved", ts: "", done: false, active: false },
    { state: "Execution Pending", label: "Execution Pending", ts: "", done: false, active: false },
    { state: "Executed", label: "Executed", ts: "", done: false, active: false },
    { state: "Settlement Pending → Reconciled", label: "Settlement Pending → Reconciled", ts: "", done: false, active: false },
  ],
};

export const TRS_INTENT_0042: TrsIntent = {
  id: "TRS-INT-0042",
  type: "Unstake",
  amount: "ETH withdrawal",
  sourceWallet: "—",
  route: "Validator route",
  createdBy: "System",
  created: "2026-09-15",
  state: "failed-terminal",
  stateLabel: "Failed",
  policyRef: "—",
  sourceFlag: "static-fixture",
  timeline: [
    { state: "Executed", label: "Executed", ts: "", done: true, active: false },
    { state: "Failed", label: "Failed — validator slashing event during withdrawal", ts: "", detail: "Funds remain locked. Contact support or retry.", done: false, active: true },
  ],
};

/* ── STK staking domain (STK-001..005) ────────────────────────────────── */

export type StkPositionStatus = "Active / Earning" | "Pending Activation" | "Unbonding";

export interface StkPosition {
  id: string;
  asset: string;
  network: string;
  walletId: string;
  validatorRoute: string;
  amount: string;
  status: StkPositionStatus;
  apy: string;
  evidenceId?: string;
  sourceFlag: TrsSource;
}

export const STK_POSITIONS: StkPosition[] = [
  { id: "STK-001", asset: "ETH", network: "Ethereum", walletId: "WLT-001", validatorRoute: "Lido via Fireblocks", amount: "500 ETH", status: "Active / Earning", apy: "3.8%", evidenceId: "EVD-006", sourceFlag: "api/backend-dependency" },
  { id: "STK-002", asset: "ETH", network: "Ethereum", walletId: "WLT-002", validatorRoute: "Lido via Mesh", amount: "200 ETH", status: "Active / Earning", apy: "3.8%", sourceFlag: "api/backend-dependency" },
  { id: "STK-003", asset: "SOL", network: "Solana", walletId: "WLT-007", validatorRoute: "Marinade via Mesh", amount: "5,000 SOL", status: "Pending Activation", apy: "6.2%", sourceFlag: "api/backend-dependency" },
  { id: "STK-004", asset: "ETH", network: "Ethereum", walletId: "WLT-005", validatorRoute: "Lido via Fireblocks", amount: "100 ETH", status: "Unbonding", apy: "—", sourceFlag: "api/backend-dependency" },
  { id: "STK-005", asset: "MATIC", network: "Polygon", walletId: "WLT-004", validatorRoute: "Direct Validator", amount: "50,000 MATIC", status: "Active / Earning", apy: "4.5%", sourceFlag: "api/backend-dependency" },
];

/* STK-001 is the position referenced by frozen evidence packet EVD-006
   ("Staking position STK-001" · linkHref "/treasury"). */

export const STK_OVERVIEW_METRICS = {
  totalStakedValue: "$4.1M",
  activePositions: "3",
  pendingActivation: "1",
  unbonding: "1",
  totalRewardsAccrued: "$23,450",
  operator: "Sai Suman Kanuganti (USR-002)",
  lastSync: "2026-09-15 10:45 UTC",
};

export interface StkRewardRow {
  position: string;
  asset: string;
  accrued: string;
  claimed: string;
  unclaimed: string;
  reconStatus: string;
  exportStatus: string;
  sourceFlag: TrsSource;
}

export const STK_REWARDS = {
  totalAccrued: "$23,450",
  claimed: "$18,200",
  unclaimed: "$5,250",
  reconciled: "$18,200",
  viewedBy: "Amina Al-Rashid (USR-006, Finance)",
  epoch: "2026-09-15 10:45 UTC",
  rows: [
    { position: "STK-001", asset: "ETH", accrued: "12.4 ETH ($31,000)", claimed: "9.8 ETH ($24,500)", unclaimed: "2.6 ETH ($6,500)", reconStatus: "Reconciled", exportStatus: "Marked", sourceFlag: "api/backend-dependency" },
    { position: "STK-002", asset: "ETH", accrued: "4.9 ETH ($12,250)", claimed: "3.2 ETH ($8,000)", unclaimed: "1.7 ETH ($4,250)", reconStatus: "Reconciled", exportStatus: "Marked", sourceFlag: "api/backend-dependency" },
    { position: "STK-003", asset: "SOL", accrued: "0 SOL", claimed: "0 SOL", unclaimed: "0 SOL", reconStatus: "Pending", exportStatus: "—", sourceFlag: "static-fixture" },
    { position: "STK-005", asset: "MATIC", accrued: "2,250 MATIC ($1,800)", claimed: "1,500 MATIC ($1,200)", unclaimed: "750 MATIC ($600)", reconStatus: "Pending Recon", exportStatus: "Marked", sourceFlag: "api/backend-dependency" },
  ] as StkRewardRow[],
};

export const STK_VALIDATOR_LIDO = {
  name: "Lido Finance",
  protocol: "Liquid Staking (stETH)",
  routes: ["Fireblocks", "Mesh"],
  networks: ["Ethereum"],
  commission: "10%",
  currentApy: "3.8%",
  totalDelegated: "9,200,000 ETH",
  yourDelegation: "700 ETH (STK-001 + STK-002)",
  uptime30d: "99.7%",
  uptime90d: "99.5%",
  slashingHistory: "None",
  lastIncident: "No incidents recorded",
  auditStatus: "Audited",
  insurance: "Covered",
  capabilityBadges: ["Liquid Staking", "MEV Boost", "Governance Voting", "Auto-compound"],
  chart: { title: "Performance Chart (30 days)", range: "3.6%–4.0%" },
  sourceFlag: "api/backend-dependency" as TrsSource,
};

export const STK_CREATE_INTENT_FORM = {
  assetOptions: ["ETH — Ethereum", "SOL — Solana", "MATIC — Polygon", "DOT — Polkadot"],
  network: "Ethereum Mainnet",
  networkHint: "Auto-selected based on asset",
  sourceWalletOptions: [
    { wallet: "WLT-001", label: "Treasury Primary", available: "1,200 ETH available" },
    { wallet: "WLT-002", label: "Treasury Secondary", available: "450 ETH available" },
    { wallet: "WLT-005", label: "Operations", available: "80 ETH available" },
  ],
  validatorRouteOptions: ["Lido via Fireblocks", "Lido via Mesh", "Rocket Pool via Fireblocks", "Direct Validator"],
  amount: "100",
  availableBalance: "1,200 ETH",
  estimatedSummary: {
    asset: "ETH",
    amount: "100 ETH",
    estimatedApy: "3.8%",
    provider: "Lido via Fireblocks",
    commission: "10%",
    activationTime: "~24 hours",
    unbondingPeriod: "~7 days",
    estAnnualReward: "3.42 ETH ($8,550)",
  },
  policyNote: "This intent requires approval per policy GOV-STAK-01.",
  permission: "treasury.staking.create",
};

export const STK_UNSTAKE_004 = {
  position: "STK-004",
  asset: "100 ETH",
  network: "Ethereum",
  wallet: "WLT-005",
  provider: "Lido via Fireblocks",
  requestedBy: "Sai Suman Kanuganti (USR-002)",
  requestedAt: "2026-09-12 14:30 UTC",
  approvalRef: "APR-2847",
  approvalGrantedAt: "2026-09-12 15:02 UTC",
  cooldownDays: 7,
  elapsedDays: 3,
  progressPct: 43,
  estimatedCompletion: "2026-09-19 14:30 UTC",
  withdrawalAvailable: "2026-09-19 ~14:30 UTC",
  dayZeroLabel: "Day 0 (Sep 12)",
  daySevenLabel: "Day 7 (Sep 19)",
  permission: "treasury.staking.withdraw",
};

/* ── LND lending domain (LND-001..005) ────────────────────────────────── */

export type LndPositionType = "Supply" | "Borrow";
export type LndPositionStatus = "Active" | "Margin Warning" | "Matured / Closed";

export interface LndPosition {
  id: string;
  type: LndPositionType;
  asset: string;
  amount: string;
  venueRoute: string;
  collateral: string;
  healthFactor: string;
  status: LndPositionStatus;
  apy: string;
  initiatedBy?: string;
  opened?: string;
  sourceFlag: TrsSource;
}

export const LND_POSITIONS: LndPosition[] = [
  { id: "LND-001", type: "Supply", asset: "USDC", amount: "200,000", venueRoute: "Aave v3 via Mesh", collateral: "—", healthFactor: "—", status: "Active", apy: "4.2%", sourceFlag: "api/backend-dependency" },
  { id: "LND-002", type: "Borrow", asset: "ETH", amount: "50", venueRoute: "Aave v3 via Mesh", collateral: "100,000 USDC", healthFactor: "1.15", status: "Margin Warning", apy: "—", initiatedBy: "Amina Al-Rashid (USR-006)", opened: "2026-09-10 14:22 UTC", sourceFlag: "api/backend-dependency" },
  { id: "LND-003", type: "Supply", asset: "USDC", amount: "100,000", venueRoute: "Compound via Fireblocks", collateral: "—", healthFactor: "—", status: "Active", apy: "3.8%", sourceFlag: "api/backend-dependency" },
  { id: "LND-004", type: "Borrow", asset: "BTC", amount: "2", venueRoute: "OTC Counterparty", collateral: "150,000 USDC", healthFactor: "2.10", status: "Active", apy: "—", sourceFlag: "api/backend-dependency" },
  { id: "LND-005", type: "Supply", asset: "ETH", amount: "100", venueRoute: "Aave v3 via Mesh", collateral: "—", healthFactor: "—", status: "Matured / Closed", apy: "—", sourceFlag: "api/backend-dependency" },
];

export const LND_OVERVIEW_METRICS = {
  totalLent: "$2,800,000",
  lendingCount: "2 positions",
  totalBorrowed: "$320K",
  borrowedCount: "2 active positions",
  avgHealthFactor: "1.63",
  marginWarnings: "1",
  marginWarningDetail: "LND-002 @ 1.15 HF",
  marginWarning: "LND-002 (Borrow 50 ETH) health factor at 1.15 — below 1.25 threshold. Initiated by Amina Al-Rashid (USR-006).",
};

export const LND_SUPPLY_INTENT = {
  asset: "USDC",
  amount: "200,000",
  venue: "Aave v3",
  route: "Mesh Connect",
  initiatedBy: "Sai Suman Kanuganti (USR-002)",
  initiatedAt: "2026-09-15 10:45 UTC",
  policyRoute: {
    requiredApprovals: "2 of 3 (Treasury Committee)",
    riskTier: "Low — DeFi Blue Chip",
    counterparty: "Aave v3 — audited, $12B TVL",
    expectedApy: "4.2%",
  },
  note: "Supply will route through Mesh Connect to Aave v3. Requires 2 approvals per policy. Source wallet: Treasury-Hot-01.",
  /* Added for LND-01 form rendering (Lending Completion phase): literal
     <option> lists transcribed verbatim from lending-wireframes.html#LND-01
     — no option was invented. sourceWalletLabel is the canonical source's
     own literal wallet label ("Treasury-Hot-01"), which does not match any
     real wallet id/name in data/mock.ts, so it is rendered as plain text,
     not linked, rather than guessing which real wallet it means. */
  assetOptions: ["USDC", "ETH", "BTC", "DAI"],
  venueOptions: ["Aave v3", "Compound", "OTC Counterparty"],
  routeOptions: ["Mesh Connect", "Fireblocks", "Direct"],
  sourceWalletLabel: "Treasury-Hot-01",
};

export const LND_BORROW_INTENT = {
  borrowAsset: "ETH",
  borrowAmount: "50",
  collateralAsset: "USDC",
  collateralAmount: "100,000",
  /* Added for LND-02 (Lending Completion phase): the canonical source's own
     "Initiated by" line was missing from this fixture on re-inspection —
     added verbatim from lending-wireframes.html#LND-02, same pattern as
     STK-03/STK-04's additive fixture completions. */
  initiatedBy: "Amina Al-Rashid (USR-006)",
  initiatedAt: "2026-09-15 10:45 UTC",
  riskParams: {
    ltv: "75%",
    liquidationThreshold: "82.5%",
    initialHealthFactor: "1.45",
    liquidationPenalty: "5%",
  },
  repaymentTerms: "Open-ended (variable rate)",
  venue: "Aave v3 via Mesh",
  note: "Borrow requires collateral deposit of 100,000 USDC before execution. Liquidation occurs if health factor drops below 1.00. Margin warning triggers at 1.25.",
  /* Added for LND-02 form rendering (Lending Completion phase): literal
     <option> lists transcribed verbatim from lending-wireframes.html#LND-02. */
  borrowAssetOptions: ["ETH", "BTC", "USDC"],
  collateralAssetOptions: ["USDC", "DAI", "ETH"],
  venueOptions: ["Aave v3 via Mesh", "Compound via Fireblocks", "OTC Counterparty"],
  repaymentTermsOptions: ["Open-ended (variable rate)", "30-day fixed", "90-day fixed", "Custom schedule"],
};

export const LND_DETAIL_002 = {
  positionId: "LND-002",
  type: "Borrow",
  assetAmount: "50 ETH (~$95,000)",
  venue: "Aave v3 via Mesh",
  initiatedBy: "Amina Al-Rashid (USR-006)",
  opened: "2026-09-10 14:22 UTC",
  statusDisposition: "margin-warning",
  healthFactor: {
    current: "1.15",
    liquidation: "1.00",
    warning: "1.25",
    safe: "2.00+",
    lastUpdated: "2026-09-15 10:42 UTC by system monitor",
    /* Literal static gauge-fill width from lending-wireframes.html#LND-03's
       own inline style (width: 57%) — not derived from a formula (it is
       not a linear position between liquidation/safe), reused as-is. */
    barPct: 57,
    sourceFlag: "api/backend-dependency" as TrsSource,
  },
  collateral: [{ asset: "USDC", amount: "100,000", valueUsd: "$100,000", weight: "100%" }],
  repaymentSchedule: [
    { date: "2026-09-10", type: "Interest", amount: "0.012 ETH", status: "repaid" },
    { date: "2026-09-15", type: "Interest", amount: "0.011 ETH", status: "due today" },
    { date: "2026-09-20", type: "Interest", amount: "~0.011 ETH", status: "upcoming" },
    { date: "Open", type: "Principal", amount: "50 ETH", status: "open-ended" },
  ],
  evidence: { count: 3, recon: "Matched — last checked 10:40 UTC" },
};

export const LND_MARGIN_INCIDENT = {
  position: "LND-002 — Borrow 50 ETH",
  venue: "Aave v3 via Mesh",
  currentHealthFactor: "1.15",
  previousHealthFactor: "1.45 (2026-09-14 22:00 UTC)",
  liquidationThreshold: "1.00",
  collateral: "100,000 USDC",
  trigger: "ETH price increase ($1,900 → $1,950)",
  triggeredAt: "2026-09-15 10:30 UTC",
  assignedTo: "Elena Vasquez (USR-004)",
  timeline: [
    { ts: "2026-09-10 14:22 UTC", desc: "Position opened — HF: 1.85", tone: "neutral" as const },
    { ts: "2026-09-12 08:15 UTC", desc: "HF adjusted to 1.65 — ETH price +3.2%", tone: "neutral" as const },
    { ts: "2026-09-14 22:00 UTC", desc: "HF dropped to 1.45 — ETH price $1,900", tone: "neutral" as const },
    { ts: "2026-09-15 06:12 UTC", desc: "HF dropped to 1.28 — approaching warning zone", tone: "warning" as const },
    { ts: "2026-09-15 10:30 UTC", desc: "Margin warning triggered — HF: 1.15 (below 1.25 threshold)", tone: "danger" as const },
  ],
  actions: ["Repay Partial (10 ETH)", "Add Collateral (+50K USDC)", "Unwind Position", "Escalate to Treasury Committee"],
  actionsNote: "All actions require approval workflow.",
};

/* ── LIQ liquidity domain (VEN-001..003 / QUO-0891 / ORD-7823) ────────── */

export type VenueStatus = "Healthy" | "Degraded";

export interface LiqVenue {
  id: string;
  name: string;
  type: "Exchange (CEX)" | "Stablecoin Issuer" | "DEX";
  status: VenueStatus;
  balance: string;
  balanceSource: TrsSource;
  readinessChecks?: { check: string; passed: boolean }[];
  incident?: {
    issue: string;
    detected: string;
    duration: string;
    affected: { id: string; type: string; impact: string }[];
  };
}

export const LIQ_VENUES: LiqVenue[] = [
  {
    id: "VEN-001",
    name: "Coinbase Prime",
    type: "Exchange (CEX)",
    status: "Degraded",
    balance: "$1,250,000",
    balanceSource: "api/backend-dependency",
    readinessChecks: [
      { check: "KYB verification complete", passed: true },
      { check: "Account provisioned", passed: true },
      { check: "API permissions granted (trade, withdraw, read)", passed: true },
      { check: "Funding account linked", passed: true },
      { check: "Sandbox testing passed (14 test trades)", passed: true },
      { check: "Production mode enabled", passed: true },
    ],
    incident: {
      issue: "Elevated API latency, 2.3% error rate",
      detected: "2026-09-15 10:38 UTC",
      duration: "7 minutes",
      affected: [
        { id: "ORD-7824", type: "Pending Order", impact: "Fill confirmation delayed" },
        { id: "WDR-0412", type: "Unsettled Withdrawal", impact: "Withdrawal API timeout" },
      ],
    },
  },
  { id: "VEN-002", name: "Circle", type: "Stablecoin Issuer", status: "Healthy", balance: "$800,000", balanceSource: "api/backend-dependency" },
  { id: "VEN-003", name: "Uniswap v3", type: "DEX", status: "Healthy", balance: "$250,000 (LP)", balanceSource: "api/backend-dependency" },
];

export const LIQ_DASHBOARD_METRICS = {
  connectedVenues: "3",
  disconnectedSub: "2 healthy, 1 degraded",
  venueExposure: "$2.3M",
  unsettled: "2",
  unsettledSub: "$48,200 pending",
  degradedVenues: "1",
  degradedSub: "VEN-001 Coinbase Prime",
};

export const LIQ_RECENT_QUOTES = [
  { time: "10:42 UTC", pair: "ETH/USDC", action: "Buy", venue: "Coinbase Prime", price: "$3,245.50", disposition: "filled" },
  { time: "10:38 UTC", pair: "BTC/USDC", action: "Sell", venue: "Circle", price: "$62,180.00", disposition: "settlement-pending" },
  { time: "10:30 UTC", pair: "USDC/DAI", action: "Swap", venue: "Uniswap v3", price: "$1.0002", disposition: "reconciled" },
].map((q) => ({ ...q, sourceFlag: "static-fixture" as TrsSource }));

export const LIQ_DISPOSITIONS = [
  "quote-expired", "order-placed", "partially-filled", "filled", "settlement-pending",
  "withdrawal-pending", "reconciled", "failed-terminal", "rejected", "cancelled", "outcome-unknown",
];

export const LIQ_QUOTE_0891 = {
  id: "QUO-0891",
  pair: "ETH/USDC",
  disposition: "quote-expired",
  error: "Venue timeout after 12s",
  venue: "VEN-001",
  sourceFlag: "api/backend-dependency" as TrsSource,
};

export const LIQ_RFQ_ROUTES = [
  { venue: "Coinbase Prime", price: "$3,245.50", slippage: "0.05%", priceImpact: "0.02%", settlement: "T+1", best: true, warning: null as string | null },
  { venue: "Circle", price: "$3,247.00", slippage: "0.08%", priceImpact: "0.03%", settlement: "T+1", best: false, warning: null },
  { venue: "Uniswap v3", price: "$3,244.80", slippage: "0.30%", priceImpact: "0.15%", settlement: "Instant", best: false, warning: "High slippage" },
].map((r) => ({ ...r, sourceFlag: "api/backend-dependency" as TrsSource }));

/* LIQ-03 Intent Review — Order was BUY 100 ETH on VEN-001. */
export const LIQ_INTENT_REVIEW = {
  action: "Buy",
  asset: "100 ETH",
  price: "$3,245.50 / ETH",
  totalCost: "$324,550.00",
  venue: "Coinbase Prime (VEN-001)",
  settlement: "T+1",
  initiatedBy: "Sai Suman Kanuganti (USR-002)",
  policyChecks: [
    { check: "Velocity limit", detail: "$324,550 of $500,000 daily limit", passed: true },
    { check: "Destination address allowlisted", detail: "", passed: true },
    { check: "Sanctions screening passed", detail: "", passed: true },
    { check: "Travel rule compliance verified", detail: "", passed: true },
  ],
  approvalNote: "Approval required: order exceeds $50,000 threshold. Requires 1 additional approver.",
  permission: "treasury:liquidity:approve",
  typedConfirm: "BUY 100 ETH",
};

export const LIQ_ORDER_7823 = {
  id: "ORD-7823",
  pair: "ETH/USDC",
  side: "Buy",
  quantity: "100 ETH",
  avgPrice: "$3,245.50",
  total: "$324,550.00",
  venue: "Coinbase Prime (VEN-001)",
  filled: "100/100 ETH",
  disposition: "withdrawal-pending",
  timeline: [
    { ts: "10:42:03 UTC", title: "Quote Accepted", detail: "$3,245.50/ETH via Coinbase Prime", disposition: "done" },
    { ts: "10:42:05 UTC", title: "Order Placed", detail: "ORD-7823 submitted (order-placed)", disposition: "done" },
    { ts: "10:42:18 UTC", title: "Partial Fill", detail: "60/100 ETH filled at $3,245.50 (partially-filled)", disposition: "done" },
    { ts: "10:42:31 UTC", title: "Fill Complete", detail: "100/100 ETH filled (filled)", disposition: "done" },
    { ts: "10:42:32 UTC", title: "Settlement Pending", detail: "Awaiting T+1 settlement (settlement-pending)", disposition: "done" },
    { ts: "Pending", title: "Withdrawal Queued", detail: "100 ETH to custody wallet (withdrawal-pending)", disposition: "active" },
    { ts: "Pending", title: "Reconciled", detail: "awaiting on-chain confirmation", disposition: "pending" },
  ],
  fills: [
    { num: 1, time: "10:42:18 UTC", qty: "60 ETH", price: "$3,245.50", cumulative: "60/100" },
    { num: 2, time: "10:42:31 UTC", qty: "40 ETH", price: "$3,245.50", cumulative: "100/100" },
  ],
  sourceFlag: "api/backend-dependency" as TrsSource,
};

/* ── TRS-03 Route Comparison (Stake 500 ETH) ──────────────────────────── */

export type TrsRouteId = "MESH_LIDO" | "FIREBLOCKS_LIDO" | "COINBASE_PRIME" | "DIRECT_VALIDATOR";

export interface TrsRoute {
  id: TrsRouteId;
  route: string;
  provider: string;
  apy: string;
  fee: string;
  lock: string;
  risk: "Low" | "High";
  policy: "Pass" | "Fail";
  recommended: boolean;
  blocked: boolean;
  blockedBy?: string;
}

export const TRS_ROUTES: TrsRoute[] = [
  { id: "MESH_LIDO", route: "Mesh → Lido", provider: "Mesh Aggregator", apy: "3.8%", fee: "0.10%", lock: "~1-5 days unstake", risk: "Low", policy: "Pass", recommended: true, blocked: false },
  { id: "FIREBLOCKS_LIDO", route: "Fireblocks → Lido", provider: "Fireblocks Custody", apy: "3.8%", fee: "0.15%", lock: "~1-5 days unstake", risk: "Low", policy: "Pass", recommended: false, blocked: false },
  { id: "COINBASE_PRIME", route: "Coinbase Prime Staking", provider: "VEN-001 · Coinbase Prime", apy: "3.2%", fee: "0.25%", lock: "~1-5 days unstake", risk: "Low", policy: "Pass", recommended: false, blocked: false },
  { id: "DIRECT_VALIDATOR", route: "Direct Validator", provider: "Self-hosted validator", apy: "4.1%", fee: "0.00%", lock: "Variable (epoch-dependent)", risk: "High", policy: "Fail", recommended: false, blocked: true, blockedBy: "GOV-STK-02" },
];

export const TRS_ROUTE_INTRO = {
  intent: "Stake 500 ETH ($900,000)",
  sourceWallet: "WLT-003",
  comparing: "4 routes",
};

/* ── TRS-04 Treasury Calendar ─────────────────────────────────────────── */

export interface TrsCalendarEvent {
  date: string;
  type: "Settlement" | "Unstaking" | "Maturity" | "Review";
  asset: string;
  amount: string;
  via: string;
  status: string;
  detail: string;
  /* Added for TRS-04 happy-state timeline rendering (Task 11): the canonical
     wireframe's timeline entry headline/narrative text, distinct from
     `status`/`detail` above which carry the *exception*-state framing for
     this same event (e.g. "Overdue") and are intentionally not rendered by
     the happy-state implementation — see the TRS-04 audit section. `wallet`
     is the real destination/receipt wallet id the canonical timeline text
     names inline, only present where the source actually names one. */
  headline: string;
  narrative: string;
  wallet?: string;
}

export const TRS_CALENDAR: TrsCalendarEvent[] = [
  { date: "Sep 16", type: "Settlement", asset: "BTC", amount: "2.0 BTC ($120,000)", via: "OTC Desk", status: "Overdue", detail: "Expected 2.0 BTC not received. Last contact with OTC desk: 10:00 UTC. Counterparty notified. Escalation in progress.", headline: "BTC Settlement", narrative: "2.0 BTC settlement from OTC trade completes. Destination:", wallet: "WLT-002" },
  { date: "Sep 20", type: "Unstaking", asset: "ETH", amount: "300 ETH ($540,000)", via: "Lido", status: "On Track", detail: "300 ETH unstaking from Lido withdrawal queue", headline: "ETH Unstaking Completes", narrative: "300 ETH unstaking from Lido withdrawal queue. Expected receipt in", wallet: "WLT-003" },
  { date: "Sep 25", type: "Maturity", asset: "USDC", amount: "200,854 USDC", via: "Aave v3", status: "—", detail: "", headline: "USDC Lending Maturity", narrative: "200,000 USDC fixed-term lending on Aave matures. Principal + 854 USDC interest." },
  { date: "Sep 30", type: "Review", asset: "—", amount: "—", via: "Internal", status: "—", detail: "Quarterly Treasury Review", headline: "Quarterly Treasury Review", narrative: "Q3 2026 treasury performance review with compliance and finance teams." },
];

/* ── Treasury policy / permission references (illustrative) ───────────── */

export interface TrsPolicyRef {
  id: string;
  label: string;
  scope: string;
}

/* These are canonical-wireframe references only: GOV-STK-01 / GOV-STK-02 /
   GOV-STAK-01 are NOT entries in the governance POL-001..006 registry and are
   intentionally not added there (frozen). They document where the canonical
   Treasury screens point. */
export const TRS_POLICY_REFS: TrsPolicyRef[] = [
  { id: "GOV-STK-01", label: "Staking intent approval policy", scope: "TRS-INT-0051 evaluated PASS on 2026-09-15 09:32 UTC" },
  { id: "GOV-STK-02", label: "Direct validator restriction", scope: "No self-hosted validators without dedicated DevOps approval — blocks Direct Validator route" },
  { id: "GOV-STAK-01", label: "Staking intent approval requirement", scope: "STK-01 create intent requires approval" },
];

export const TRS_PERMISSION_REFS = [
  "treasury.staking.create",
  "treasury.staking.withdraw",
  "treasury.rewards.claim",
  "treasury:liquidity:read",
  "treasury:liquidity:trade",
  "treasury:liquidity:approve",
  "treasury:venue:manage",
  "treasury:incident:manage",
  "treasury:execution:read",
];
